import type { Task } from "@app/core";
import { Check, cn, CornerDownRight, MapPin } from "@app/ui";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatDurationCompact } from "../../lib/format.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useLabelsStore, useSettingsStore, useTasksStore } from "../../stores/index.js";
import { MarkdownPreview } from "../notes/MarkdownPreview.js";
import { contrastShade, effectiveColorOf, pastel } from "../task-colors.js";
import { useMatrixPriorityColor } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { occurrenceRefOf } from "../../lib/virtual-id.js";
import { ALL_VISIBLE, BLOCK_V_CHROME_PX, BLOCK_V_PAD_PX, fitSections, fitTitle } from "./block-content-fit.js";
import { coverShape, type CoverRect } from "./cover-shape.js";
import { SubtaskRows } from "./SubtaskRows.js";
import { useCalendarChildInfo, useCalendarInheritedTask, useCalendarRenderIndex } from "./CalendarRenderIndex.js";
import { isMobileUx } from "../../lib/useMobileUx.js";
import { mobileBlockStyle, type MobileBlockStyle } from "../mobile/mobile-block-style.js";

interface Props {
  task: Task;
  /** Rendered block height in px (drives which sections fit). */
  blockHeightPx: number;
  /** Rendered inner content width in px (for the flow-around reserve). */
  blockWidthPx: number;
  /** Hover-expanded: show everything, ignore the budget and the cover. */
  expanded: boolean;
  isContinuation?: boolean;
  /** The front block(s) covering this one's right side; content flows around
   *  the right-anchored staircase they form. */
  cover?: { leftPx: number; topPx: number; bottomPx: number; rects: CoverRect[] } | undefined;
  /** Reports whether expanding would reveal more than the collapsed view shows. */
  onExpandableChange?: (expandable: boolean) => void;
  /** When true (calendar blocks), sub-subtasks auto-expand if they fit the block
   *  without pushing other content out. Off for compact surfaces (inbox). */
  autoExpandSubtasks?: boolean;
  /** Mobile style already computed by the owning TaskBlock. */
  mobileStyle?: MobileBlockStyle | null;
}

function readableOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#fff";
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#1f2937" : "#fff";
}

export function TaskBlockContent({ task, blockHeightPx, blockWidthPx, expanded, isContinuation, cover, onExpandableChange, autoExpandSubtasks, mobileStyle }: Props) {
  const index = useCalendarRenderIndex();
  const tf = useTimeFormat();
  const allLabels = useLabelsStore((s) => s.labels);
  const labelById = useMemo(() => new Map(allLabels.map((label) => [label.id, label])), [allLabels]);
  const complete = useTasksStore((s) => s.complete);
  const completeOccurrence = useTasksStore((s) => s.completeOccurrence);
  const done = task.status === "done";

  function toggleDone(e: React.MouseEvent) {
    e.stopPropagation();
    if (task.virtual && task.parent_id) {
      void completeOccurrence(task.parent_id, occurrenceRefOf(task), !done);
    } else {
      void complete(task.id, !done);
    }
  }
  const rootRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const [titlePx, setTitlePx] = useState(0);

  const start = task.due_at ? new Date(task.due_at) : null;
  const end = start && task.duration_minutes > 0
    ? new Date(start.getTime() + task.duration_minutes * 60_000)
    : null;
  const timeText = start
    ? end
      ? `${tf.hmDate(start)} – ${tf.hmDate(end)} (${formatDurationCompact(task.duration_minutes)})`
      : tf.hmDate(start)
    : task.duration_minutes > 0
      ? formatDurationCompact(task.duration_minutes) // undated (inbox): show duration only
      : "";

  const labels = task.label_ids
    .map((id) => labelById.get(id))
    .filter((l): l is NonNullable<typeof l> => !!l);

  const hasLocation = task.location.trim().length > 0;
  const hasNotes = task.notes.trim().length > 0;
  const hasTags = labels.length > 0;

  // Subtasks: undated children render inside this block; the badge counts ALL
  // direct children. A child block shows a marker tinted from its parent.
  const { children: kids, progress } = useCalendarChildInfo(task.id);
  const undatedKids = kids.filter((k) => !k.due_at);
  // Subtask rows get a slightly-brighter shade of this block's effective color.
  const contextColor = index.contextColorOf(task);
  const inheritedTask = useCalendarInheritedTask(task);
  // In matrix scope a PLACED block's own color is the continuous diagonal hue,
  // so the subtask-row accent tint tracks the exact position (not the 3-band
  // priority).
  const priorityColor = useMatrixPriorityColor(inheritedTask);
  const ownColor = effectiveColorOf(inheritedTask, [inheritedTask], 0, contextColor, priorityColor);
  const subtaskAccent = ownColor ? pastel(ownColor, 0.3) : undefined;
  // Mobile-only: topic-gradient title + section colors stepping toward the
  // calendar/custom color (desktop keeps the auto-contrast wash text).
  const mob = mobileStyle !== undefined ? mobileStyle : isMobileUx()
    ? mobileBlockStyle({
        priority: task.priority,
        matrixColor: priorityColor,
        customColor: inheritedTask.custom_color,
        topicColors: index.topicColorsOf(task),
        calendarColor: index.calendarColorOf(task),
        isEvent: task.is_event,
      })
    : null;
  const isSubtask = !!task.subtask_parent_id;
  const hasNested = undatedKids.some((k) => useTasksStore.getState().tasks.some((g) => !g.virtual && !g.deleted_at && !g.due_at && g.subtask_parent_id === k.id));
  // Optimistically show nested sub-subtasks; the layout effect collapses them if
  // they don't fit. Reset (re-try) when the block size or subtask set changes.
  const [nestedFits, setNestedFits] = useState(true);
  const showNested = expanded || (!!autoExpandSubtasks && nestedFits);
  useLayoutEffect(() => { setNestedFits(true); }, [task.id, blockHeightPx, undatedKids.length, hasNested]);

  // Very short block: TaskBlock collapses the wrapper's vertical padding toward
  // 0 first (same fitTitle call), then the title font shrinks (down to a floor)
  // so it still shows a full line instead of clipping mid-glyph. Inline px
  // font-size; the checkbox, markers and badge inside the title are em-sized so
  // they shrink along.
  const textScale = useSettingsStore((s) => s.textScale);
  const collapsedFit = fitTitle(blockHeightPx, textScale);
  const collapsedBudget = blockHeightPx - BLOCK_V_CHROME_PX - 2 * (collapsedFit.padPx ?? BLOCK_V_PAD_PX);
  const titleFontPx = expanded ? null : collapsedFit.fontPx;

  const collapsedVisible = fitSections({ budgetPx: collapsedBudget, titlePx, hasLocation, hasNotes, hasTags });
  const visible = expanded ? ALL_VISIBLE : collapsedVisible;

  // No dependency array on purpose — see `docs/calendar-view.md`. The geometry
  // READS here are free (measured: keeping them and discarding the results costs
  // exactly what deleting the effect entirely costs); what is not free is the
  // extra render pass their state updates schedule.
  useLayoutEffect(() => {
    if (titleRef.current) setTitlePx(titleRef.current.offsetHeight);
    if (expanded) return;
    const el = rootRef.current;
    const overflow = !!el && el.scrollHeight > el.clientHeight + 1;
    // Sub-subtasks are shown by default; if that overflows the block, hide them so
    // they never push out other content (then the block becomes hover-expandable).
    if (autoExpandSubtasks && nestedFits && hasNested && overflow) { setNestedFits(false); return; }
    if (onExpandableChange) onExpandableChange(overflow || (!showNested && hasNested));
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // Flow-around reserve for the covering block(s): a right-anchored staircase
  // carved out with a polygon so text fills the free area beside each band.
  let floatStyle: React.CSSProperties | undefined;
  const cs = !expanded && cover ? coverShape(cover.rects, blockWidthPx) : null;
  if (cs) {
    floatStyle = { float: "right", width: blockWidthPx, height: Math.round(cs.heightPx), shapeOutside: cs.shape };
  }

  return (
    // `data-block-content`: TaskBlock measures this element's scrollHeight for
    // the hover-expand target (the colored wrapper clips nothing itself — the
    // overflow lives HERE, behind this root's own overflow-hidden).
    <div ref={rootRef} data-block-content className="h-full overflow-hidden">
      {floatStyle && <div aria-hidden style={floatStyle} />}

      {/* `data-block-title` = the nest-drop dwell zone (useNestDrop). */}
      <div ref={titleRef} data-block-title className="break-words text-xs font-medium leading-tight" style={titleFontPx != null ? { fontSize: titleFontPx } : undefined}>
        {!isContinuation && !task.is_event && (
          <button
            type="button"
            onClick={toggleDone}
            onMouseDown={(e) => e.stopPropagation()}
            draggable={false}
            aria-label={done ? "Mark incomplete" : "Mark complete"}
            style={ownColor ? { color: contrastShade(ownColor), borderColor: contrastShade(ownColor) } : undefined}
            className={`group/btn mr-1 inline-flex h-[1em] w-[1em] shrink-0 items-center justify-center rounded-full border-[1.5px] border-current align-[-1px] transition-colors ${done ? "bg-current/25" : "bg-transparent hover:bg-current/20"}`}
          >
            <Check className={`h-[0.67em] w-[0.67em] transition-opacity duration-150 ${done ? "opacity-100" : "opacity-0 group-hover/btn:opacity-40"}`} />
          </button>
        )}
        {isSubtask && !isContinuation && <CornerDownRight className="mr-0.5 inline h-[0.67em] w-[0.67em] align-[-1px] opacity-60" />}
        {isContinuation && <span className="mr-0.5 text-[0.75em] opacity-60">↑</span>}
        {mob ? <span style={mob.titleText}>{task.title}</span> : task.title}
        {progress.total > 0 && (
          <span className="ml-1 rounded bg-black/10 px-1 text-[0.75em] font-normal tabular-nums dark:bg-white/15">
            {progress.done}/{progress.total}
          </span>
        )}
      </div>

      {visible.time && timeText && (
        <div className={cn("break-words font-mono text-[calc(10px*var(--app-text-scale))] leading-tight", mob ? "" : "opacity-80", expanded && !collapsedVisible.time && "block-reveal")} style={mob ? { color: mob.metaColors.time } : undefined}>{timeText}</div>
      )}

      {visible.location && hasLocation && (
        <div className={cn("break-words text-[calc(10px*var(--app-text-scale))] leading-tight", mob ? "" : "opacity-70", expanded && !collapsedVisible.location && "block-reveal")} style={mob ? { color: mob.metaColors.location } : undefined}>
          <MapPin className="mr-0.5 inline h-2.5 w-2.5 align-[-1px]" />{task.location}
        </div>
      )}

      {visible.notes && hasNotes && (
        <MarkdownPreview
          value={task.notes}
          compact
          className={cn("text-[calc(10px*var(--app-text-scale))] leading-tight", mob ? "" : "opacity-70", expanded && !collapsedVisible.notes && "block-reveal")}
          style={mob ? { color: mob.metaColors.notes } : undefined}
        />
      )}

      {visible.tags && hasTags && (
        <div className={cn("mt-0.5 leading-[calc(16px*var(--app-text-scale))]", expanded && !collapsedVisible.tags && "block-reveal")}>
          {labels.map((l) => {
            const bg = l.color ?? "#64748b";
            return (
              <span key={l.id} className="mr-0.5 inline-block rounded px-1 text-[calc(9px*var(--app-text-scale))] leading-[calc(14px*var(--app-text-scale))]" style={{ backgroundColor: bg, color: readableOn(bg) }}>
                {l.name}
              </span>
            );
          })}
        </div>
      )}

      {!isContinuation && <SubtaskRows parentId={task.id} children={undatedKids} accent={subtaskAccent} expanded={showNested} />}
    </div>
  );
}
